// =============================================================================
// Composant FileUpload - Design Apple élégant et moderne
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { AlertCircle, FileUp } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  url: string;
}

interface FileUploadProps {
  onFilesUpload: (files: UploadedFile[]) => void;
  acceptedTypes?: Record<string, string[]>;
  maxSize?: number;
  maxFiles?: number;
  className?: string;
  // Nouvelle prop pour personnaliser l'affichage des types de fichiers
  fileTypeDisplay?: string;
}

const FileUpload = ({
  onFilesUpload,
  acceptedTypes = {
    'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/*': ['.txt', '.md'],
  },
  maxSize = 10 * 1024 * 1024, // 10MB
  maxFiles = 5,
  className,
  fileTypeDisplay,
}: FileUploadProps) => {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const maxSizeMb = useMemo(() => Math.round((maxSize / (1024 * 1024)) * 10) / 10, [maxSize]);

  // Fonction pour générer l'affichage des types de fichiers
  const getFileTypesDisplay = (): string => {
    if (fileTypeDisplay) {
      return fileTypeDisplay;
    }

    // Génération automatique basée sur acceptedTypes
    const extensions: string[] = [];
    Object.values(acceptedTypes).forEach(exts => {
      extensions.push(...exts);
    });

    // Supprimer les doublons et formater
    const uniqueExtensions = Array.from(new Set(extensions));
    return uniqueExtensions.map(ext => ext.toUpperCase().replace('.', '')).join(', ');
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setUploadError(null);

      if (rejectedFiles.length > 0) {
        const error = rejectedFiles[0].errors[0];
        if (error.code === 'file-too-large') {
          setUploadError(`Fichier trop volumineux. Taille maximale: ${maxSizeMb} MB`);
        } else if (error.code === 'file-invalid-type') {
          setUploadError('Type de fichier non autorisé');
        } else if (error.code === 'too-many-files') {
          setUploadError(`Trop de fichiers. Maximum autorisé: ${maxFiles} fichiers`);
        }
        return;
      }

      const uploadedFiles: UploadedFile[] = acceptedFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file),
      }));

      onFilesUpload(uploadedFiles);
    },
    [onFilesUpload, maxFiles, maxSizeMb]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    maxSize,
    maxFiles,
    multiple: maxFiles > 1,
  });

  const getHeading = () => {
    if (isDragActive) {
      return isDragReject
        ? 'Fichier non autorisé'
        : 'Déposez vos fichiers ici';
    }
    return 'Glissez-déposez vos fichiers ici';
  };

  return (
    <div className={cn('w-full', className)}>
      <div
        {...getRootProps()}
        className={cn(
          // Base styles avec glassmorphism moderne
          "relative overflow-hidden rounded-2xl p-12 text-center cursor-pointer",
          "transition-all duration-500 ease-out",
          "bg-gradient-to-br from-white/95 via-slate-50/90 to-blue-50/80",
          "backdrop-blur-xl border border-white/60",
          "shadow-lg hover:shadow-2xl",
          "transform hover:-translate-y-1 hover:scale-[1.02]",
          
          // États interactifs
          isDragActive && !isDragReject && [
            "border-emerald-300/80 bg-gradient-to-br from-emerald-50/95 via-white/90 to-emerald-50/80",
            "shadow-emerald-200/50 shadow-2xl scale-[1.02] -translate-y-1"
          ],
          isDragReject && [
            "border-red-300/80 bg-gradient-to-br from-red-50/95 via-white/90 to-red-50/80",
            "shadow-red-200/50 shadow-2xl"
          ],
          !isDragActive && [
            "hover:border-blue-300/60 hover:bg-gradient-to-br hover:from-blue-50/95 hover:via-white/90 hover:to-indigo-50/80",
            "hover:shadow-blue-200/30"
          ]
        )}
        style={{
          boxShadow: isDragActive && !isDragReject 
            ? '0 20px 40px rgba(16, 185, 129, 0.15), 0 0 0 1px rgba(16, 185, 129, 0.1)'
            : isDragReject
            ? '0 20px 40px rgba(239, 68, 68, 0.15), 0 0 0 1px rgba(239, 68, 68, 0.1)'
            : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.6)'
        }}
      >
        <input {...getInputProps()} />
        
        {/* Effet de brillance subtil */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%]" />
        
        <div className="relative z-10 flex flex-col items-center space-y-6">
          {/* Icône principale avec design moderne */}
          <div className="relative">
            <div className={cn(
              "w-20 h-20 rounded-2xl flex items-center justify-center",
              "transition-all duration-500 ease-out",
              "bg-gradient-to-br from-white to-slate-100/80",
              "shadow-lg border border-white/60",
              "group-hover:scale-110 group-hover:rotate-3",
              isDragActive && !isDragReject && [
                "bg-gradient-to-br from-emerald-100 to-emerald-50",
                "border-emerald-200/60 shadow-emerald-200/30"
              ],
              isDragReject && [
                "bg-gradient-to-br from-red-100 to-red-50",
                "border-red-200/60 shadow-red-200/30"
              ]
            )}>
              {isDragReject ? (
                <AlertCircle className={cn(
                  "h-10 w-10 transition-all duration-300",
                  "text-red-500 group-hover:scale-110"
                )} />
              ) : (
                <FileUp className={cn(
                  "h-10 w-10 transition-all duration-300",
                  isDragActive ? "text-emerald-600" : "text-blue-500",
                  "group-hover:scale-110 group-hover:text-blue-600"
                )} />
              )}
            </div>
            
            {/* Effet de pulsation pour l'état actif */}
            {isDragActive && !isDragReject && (
              <div className="absolute inset-0 rounded-2xl bg-emerald-400/20 animate-pulse" />
            )}
          </div>
          
          {/* Contenu textuel avec typographie améliorée */}
          <div className="space-y-3 max-w-md">
            <h3 className={cn(
              "text-xl font-semibold leading-tight tracking-tight",
              "transition-colors duration-300",
              isDragActive && !isDragReject ? "text-emerald-700" : 
              isDragReject ? "text-red-700" : "text-slate-800",
              "group-hover:text-slate-900"
            )}>
              {getHeading()}
            </h3>
            
            <p className={cn(
              "text-sm font-medium text-slate-600/80",
              "transition-colors duration-300",
              "group-hover:text-slate-700"
            )}>
              Cliquez pour sélectionner ou glissez-déposez vos fichiers
            </p>
            
            <div className="flex items-center justify-center gap-2 pt-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200/60">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-medium text-slate-700">
                  {getFileTypesDisplay()}
                </span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200/60">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-xs font-medium text-slate-700">
                  Max {maxSizeMb} MB
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {uploadError && (
        <div className="mt-6 p-4 bg-gradient-to-r from-red-50/95 to-red-100/80 backdrop-blur-sm border border-red-200/60 rounded-xl shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-sm font-medium text-red-700 leading-relaxed">
              {uploadError}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;